// ─── Frontend Logger — Singleton, batch POST, auto-captures ─────────
// Pattern from logs.md — adapted for Browser Streaming frontend

export interface LogEntry {
  ts: string;
  type: "api" | "nav" | "action" | "debug" | "info" | "warn" | "error";
  user: string | null;
  sess: string;
  nav: string;
  src: string;
  msg?: string;
  data?: Record<string, unknown>;
  stack?: string;
  method?: string;
  endpoint?: string;
  status?: number;
  ms?: number;
  action?: string;
}

type Subscriber = (entries: LogEntry[], latest: LogEntry | null) => void;

const MAX_BUFFER = 500;
const FLUSH_INTERVAL_MS = 5000;
const MAX_RETRY_ENTRIES = 50;

// ─── Session ID ─────────────────────────────────────────────────────

function makeSessionId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 6);
  return `${ts}-${rand}`.slice(0, 8);
}

// ─── Logger class ───────────────────────────────────────────────────

class Logger {
  private buffer: LogEntry[] = [];
  private pendingFlush: LogEntry[] = [];
  private subscribers: Subscriber[] = [];
  private readonly sessId = makeSessionId();
  private nav1 = "-";
  private nav2 = "-";
  private nav3 = "-";
  private nav4 = "-";
  private username: string | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private origConsoleError: typeof console.error;
  private origConsoleWarn: typeof console.warn;
  private tokenGetter: (() => string | null) | null = null;

  constructor() {
    this.origConsoleError = console.error;
    this.origConsoleWarn = console.warn;
    this.setupAutoCaptures();
    this.startFlushTimer();
  }

  // ── Navigation context ──────────────────────────────────────────

  setNav1(v: string) { this.nav1 = v; }
  setNav2(v: string) { this.nav2 = v; }
  setNav3(v: string) { this.nav3 = v; }
  setNav4(v: string) { this.nav4 = v; }
  setUser(u: string | null) { this.username = u; }
  setTokenGetter(fn: () => string | null) { this.tokenGetter = fn; }

  private get navStr(): string {
    return `${this.nav1}.${this.nav2}.${this.nav3}.${this.nav4}`;
  }

  // ── Core logging methods ────────────────────────────────────────

  private push(entry: LogEntry) {
    this.buffer.push(entry);
    if (this.buffer.length > MAX_BUFFER) {
      this.buffer.shift();
    }
    this.notify(entry);

    // Flush immediately on errors
    if (entry.type === "error") {
      this.flush();
    }
  }

  private makeEntry(
    type: LogEntry["type"],
    src: string,
    extra: Partial<LogEntry> = {},
  ): LogEntry {
    const now = new Date();
    const ts = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}.${now.getMilliseconds().toString().padStart(3, "0")}`;
    return {
      ts,
      type,
      user: this.username,
      sess: this.sessId,
      nav: this.navStr,
      src,
      ...extra,
    };
  }

  // ── Public API ──────────────────────────────────────────────────

  info(msg: string, data?: Record<string, unknown>) {
    this.push(this.makeEntry("info", this._currentSrc, { msg, data }));
  }

  warn(msg: string, data?: Record<string, unknown>) {
    this.push(this.makeEntry("warn", this._currentSrc, { msg, data }));
  }

  error(msg: string, data?: Record<string, unknown>, stack?: string) {
    this.push(this.makeEntry("error", this._currentSrc, { msg, data, stack }));
  }

  debug(msg: string, data?: Record<string, unknown>) {
    this.push(this.makeEntry("debug", this._currentSrc, { msg, data }));
  }

  action(actionName: string, data?: Record<string, unknown>) {
    this.push(
      this.makeEntry("action", this._currentSrc, {
        action: actionName,
        data,
      }),
    );
  }

  api(
    method: string,
    endpoint: string,
    status: number,
    ms: number,
    data?: Record<string, unknown>,
  ) {
    this.push(
      this.makeEntry("api", this._currentSrc, {
        method,
        endpoint,
        status,
        ms,
        data,
      }),
    );
  }

  nav(target: string) {
    this.push(this.makeEntry("nav", "nav", { msg: target }));
  }

  // ── Scoped loggers ─────────────────────────────────────────────

  private _currentSrc = "app";

  // ── Subscribe ───────────────────────────────────────────────────

  subscribe(fn: Subscriber): () => void {
    this.subscribers.push(fn);
    return () => {
      this.subscribers = this.subscribers.filter((s) => s !== fn);
    };
  }

  getBuffer(): LogEntry[] {
    return [...this.buffer];
  }

  private notify(latest: LogEntry) {
    for (const fn of this.subscribers) {
      try {
        fn(this.buffer, latest);
      } catch {
        // ignore subscriber errors
      }
    }
  }

  // ── Auto-captures ──────────────────────────────────────────────

  private setupAutoCaptures() {
    // console.error capture
    console.error = (...args: unknown[]) => {
      this.origConsoleError.apply(console, args);
      const msg = args
        .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
        .join(" ")
        .slice(0, 500);
      this.push(this.makeEntry("error", "console", { msg }));
    };

    // console.warn capture
    console.warn = (...args: unknown[]) => {
      this.origConsoleWarn.apply(console, args);
      const msg = args
        .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
        .join(" ")
        .slice(0, 500);
      this.push(this.makeEntry("warn", "console", { msg }));
    };

    // window.onerror
    window.addEventListener("error", (ev) => {
      this.push(
        this.makeEntry("error", "window", {
          msg: ev.message,
          data: {
            filename: ev.filename,
            lineno: ev.lineno,
            colno: ev.colno,
          },
          stack: ev.error?.stack?.slice(0, 1000),
        }),
      );
    });

    // unhandledrejection
    window.addEventListener("unhandledrejection", (ev) => {
      const msg =
        ev.reason instanceof Error
          ? ev.reason.message
          : String(ev.reason).slice(0, 500);
      const stack =
        ev.reason instanceof Error
          ? ev.reason.stack?.slice(0, 1000)
          : undefined;
      this.push(this.makeEntry("error", "promise", { msg, stack }));
    });

    // Click tracking (buttons and [data-track])
    document.addEventListener(
      "click",
      (ev) => {
        const target = ev.target as HTMLElement;
        const btn = target.closest("button, [data-track]") as HTMLElement | null;
        if (!btn) return;
        const label =
          btn.getAttribute("data-track") ||
          btn.textContent?.trim().slice(0, 50) ||
          btn.tagName;
        this.push(this.makeEntry("action", "click", { action: label }));
      },
      true,
    );

    // Flush on unload / visibility change
    window.addEventListener("beforeunload", () => this.flush());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") this.flush();
    });
  }

  // ── Flush to backend ───────────────────────────────────────────

  private startFlushTimer() {
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  flush() {
    const entries = this.buffer.splice(0);
    if (entries.length === 0 && this.pendingFlush.length === 0) return;

    const toSend = [...this.pendingFlush.slice(-MAX_RETRY_ENTRIES), ...entries];
    this.pendingFlush = [];

    const body = JSON.stringify({ logs: toSend });

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const token = this.tokenGetter?.();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      fetch("/api/logs", {
        method: "POST",
        headers,
        body,
        keepalive: true,
      }).catch(() => {
        // On failure, keep last entries for retry
        this.pendingFlush = toSend.slice(-MAX_RETRY_ENTRIES);
      });
    } catch {
      this.pendingFlush = toSend.slice(-MAX_RETRY_ENTRIES);
    }
  }

  destroy() {
    if (this.flushTimer) clearInterval(this.flushTimer);
    console.error = this.origConsoleError;
    console.warn = this.origConsoleWarn;
  }
}

// ─── Singleton ──────────────────────────────────────────────────────

let _instance: Logger | null = null;

function getLogger(): Logger {
  if (!_instance) {
    _instance = new Logger();
  }
  return _instance;
}

/** Create a scoped logger for a specific module. */
export function createLogger(src: string) {
  const logger = getLogger();
  return {
    info: (msg: string, data?: Record<string, unknown>) => {
      logger["_currentSrc"] = src;
      logger.info(msg, data);
    },
    warn: (msg: string, data?: Record<string, unknown>) => {
      logger["_currentSrc"] = src;
      logger.warn(msg, data);
    },
    error: (msg: string, data?: Record<string, unknown>, stack?: string) => {
      logger["_currentSrc"] = src;
      logger.error(msg, data, stack);
    },
    debug: (msg: string, data?: Record<string, unknown>) => {
      logger["_currentSrc"] = src;
      logger.debug(msg, data);
    },
    action: (actionName: string, data?: Record<string, unknown>) => {
      logger["_currentSrc"] = src;
      logger.action(actionName, data);
    },
    api: (
      method: string,
      endpoint: string,
      status: number,
      ms: number,
      data?: Record<string, unknown>,
    ) => {
      logger["_currentSrc"] = src;
      logger.api(method, endpoint, status, ms, data);
    },
    nav: (target: string) => logger.nav(target),
    setNav1: (v: string) => logger.setNav1(v),
    setNav2: (v: string) => logger.setNav2(v),
    setNav3: (v: string) => logger.setNav3(v),
    setNav4: (v: string) => logger.setNav4(v),
    setUser: (u: string | null) => logger.setUser(u),
    subscribe: (fn: Subscriber) => logger.subscribe(fn),
    getBuffer: () => logger.getBuffer(),
    flush: () => logger.flush(),
  };
}

export const logger = getLogger();
export default logger;

// ─── T102: Tool usage logger (CDP / a11y / visual classification) ───

export type ToolFamily = "cdp" | "a11y" | "visual";

export interface ToolLogEntry {
  tool_used: string;
  tool_family: ToolFamily;
  ts: number;
}

/**
 * Classify a tool name into its family.
 * 12e occurrence pattern "extract pure fn + test" — TypeScript × 2.
 */
export function getToolFamily(toolName: string): ToolFamily {
  if (toolName.startsWith("a11y.")) return "a11y";
  if (
    toolName.startsWith("mouse.") ||
    toolName.startsWith("keyboard.") ||
    toolName.startsWith("window.") ||
    toolName === "screen.capture" ||
    toolName === "text.ocr"
  ) return "visual";
  return "cdp";
}

const FAMILY_PRIORITY: readonly ToolFamily[] = ["cdp", "a11y", "visual"] as const;

/**
 * Compute the next tool family after N consecutive failures.
 * Returns null if threshold not reached or already at lowest priority (visual).
 * 13e occurrence pattern "extract pure fn + test" — TypeScript × 3.
 *
 * @param currentFamily  famille outil en cours
 * @param consecutiveErrors  nb d'erreurs consécutives pour cette famille
 * @param threshold  seuil avant downgrade (défaut 2)
 */
export function nextFamilyOnFailure(
    currentFamily: ToolFamily,
    consecutiveErrors: number,
    threshold = 2,
): ToolFamily | null {
    if (consecutiveErrors < threshold) return null;
    const idx = FAMILY_PRIORITY.indexOf(currentFamily);
    if (idx < 0 || idx >= FAMILY_PRIORITY.length - 1) return null;
    return FAMILY_PRIORITY[idx + 1] ?? null;
}

const _toolLog: ToolLogEntry[] = [];

/** Record a tool call. Called from handleToolsCall (handlers.ts). */
export function logToolUse(toolName: string): void {
  _toolLog.push({ tool_used: toolName, tool_family: getToolFamily(toolName), ts: Date.now() });
}

/** Return a snapshot copy of the log. Does not clear. */
export function getToolLog(): ToolLogEntry[] {
  return [..._toolLog];
}

/** Clear the tool log (call between sessions). */
export function clearToolLog(): void {
  _toolLog.length = 0;
}
